import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { X, ShieldCheck, AlertCircle, CheckCircle2, Save, Loader2, Eye } from "lucide-react";

/**
 * HubSpotMappingModal
 *
 * The "field-mapping confirmation screen" Joey asked for. Shows:
 *  - Pipeline + stage → funnel-bucket mapping (multi-select per stage)
 *  - hs_analytics_source value → pie source mapping
 *  - 5 most-recent deals as a sanity preview
 *  - "Save mapping" (signs off but stays read-only)
 *  - "Save & Activate as primary" (turns Leads + Recovery to use HubSpot data)
 *
 * Buckets per stage are stored as an array because a single stage (Meeting Sat,
 * Nurture follow-ups) can land in both Held AND Qualified per our funnel def.
 */
const ALL_SOURCES = ["Google Ads", "Meta Ads", "Organic", "Referral", "Direct", "Other"];

const QUAL_MODES = [
  { id: "none", label: "C — Don't derive from HubSpot",
    desc: "Keep Slack/Sheet as the canonical qualified-calls count. Recommended if your team doesn't tag qualified in HubSpot." },
  { id: "outcome", label: "B — Derive from outcome",
    desc: "Count any deal that hit Closed Won or Closed Lost (non-DQ) this month. Understates open-but-qualified pipeline." },
  { id: "property", label: "A — Manual HubSpot property",
    desc: "Read a specific deal property your team marks after the call. Pick the property + values that mean 'qualified'." },
];

export default function HubSpotMappingModal({ open, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [bucketDefs, setBucketDefs] = useState([]);
  const [preview, setPreview] = useState([]);
  const [defaultSrcMap, setDefaultSrcMap] = useState({});
  const [mapping, setMapping] = useState(null);
  const [dealProps, setDealProps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // Auto-pick "Deals pipeline" if present, else first
  const selectedPipeline = useMemo(() => {
    if (!mapping?.pipeline_id) return pipelines[0] || null;
    return pipelines.find((p) => p.id === mapping.pipeline_id) || pipelines[0] || null;
  }, [pipelines, mapping?.pipeline_id]);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(null); setSaveMsg(null);
    Promise.all([
      api.get("/integrations/hubspot/pipelines"),
      api.get("/integrations/hubspot/preview").catch(() => ({ data: { deals: [], default_source_map: {} } })),
      api.get("/integrations/hubspot/mapping"),
      api.get("/integrations/hubspot/deal-properties").catch(() => ({ data: { properties: [] } })),
    ]).then(([pRes, prevRes, mRes, dpRes]) => {
      const pls = pRes.data.pipelines || [];
      setPipelines(pls);
      setBucketDefs(pRes.data.buckets || []);
      setPreview(prevRes.data.deals || []);
      setDefaultSrcMap(prevRes.data.default_source_map || {});
      setDealProps(dpRes.data.properties || []);
      // Hydrate mapping: if user has no saved stage_buckets yet, seed from defaults
      let m = mRes.data || {};
      const chosenPipeline = pls.find((p) => p.label?.toLowerCase().includes("deals")) || pls[0];
      if (!m.stage_buckets || Object.keys(m.stage_buckets).length === 0) {
        const seed = {};
        if (chosenPipeline) {
          for (const s of chosenPipeline.stages) {
            seed[s.id] = s.default_buckets || ["none"];
          }
        }
        m = {
          ...m,
          pipeline_id: chosenPipeline?.id || null,
          pipeline_label: chosenPipeline?.label || null,
          stage_buckets: seed,
          source_value_map: m.source_value_map || prevRes.data.default_source_map || {},
        };
      }
      // Ensure qualified_def always present
      if (!m.qualified_def) m.qualified_def = { mode: "none" };
      setMapping(m);
    }).catch((e) => {
      setError(e?.response?.data?.detail || "Failed to load HubSpot pipelines. Confirm the token has all required scopes.");
    }).finally(() => setLoading(false));
  }, [open]);

  const switchPipeline = (pid) => {
    const p = pipelines.find((x) => x.id === pid);
    if (!p) return;
    const seed = {};
    for (const s of p.stages) {
      seed[s.id] = s.default_buckets || ["none"];
    }
    setMapping((m) => ({ ...m, pipeline_id: p.id, pipeline_label: p.label, stage_buckets: seed }));
  };

  const toggleBucketForStage = (stageId, bucket) => {
    setMapping((m) => {
      const cur = new Set(m.stage_buckets?.[stageId] || []);
      if (cur.has(bucket)) cur.delete(bucket); else cur.add(bucket);
      if (cur.size === 0) cur.add("none");
      else if (cur.size > 1 && cur.has("none")) cur.delete("none");
      return { ...m, stage_buckets: { ...m.stage_buckets, [stageId]: Array.from(cur) } };
    });
  };

  const updateSourceMap = (hsVal, ourVal) => {
    setMapping((m) => ({ ...m, source_value_map: { ...m.source_value_map, [hsVal]: ourVal } }));
  };

  const save = async (activate = false) => {
    setBusy(true); setSaveMsg(null);
    try {
      const payload = {
        pipeline_id: mapping.pipeline_id,
        pipeline_label: mapping.pipeline_label,
        stage_buckets: mapping.stage_buckets,
        source_property: mapping.source_property || "hs_analytics_source",
        source_value_map: mapping.source_value_map,
        qualified_def: mapping.qualified_def || { mode: "none" },
        confirmed: true,
        activate_as_primary: activate,
      };
      const { data } = await api.post("/integrations/hubspot/mapping", payload);
      setMapping(data);
      setSaveMsg({ kind: "ok", text: activate ? "Mapping saved & HubSpot activated as primary source." : "Mapping saved. (Not yet active — Leads/Recovery still use sheets.)" });
      onSaved && onSaved(data);
    } catch (e) {
      setSaveMsg({ kind: "err", text: e?.response?.data?.detail || "Save failed" });
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="hubspot-mapping-modal">
      <div className="bg-warm w-full max-w-5xl border-t-4 border-yellow shadow-xl">
        <div className="bg-navy text-warm px-6 py-4 flex items-center justify-between border-b-4 border-yellow">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-yellow opacity-90">HubSpot · field-mapping confirmation</div>
            <h2 className="text-xl font-bold mt-1">Sign off on every HubSpot → app mapping</h2>
          </div>
          <button onClick={onClose} className="text-warm hover:text-yellow p-1" data-testid="hubspot-mapping-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading && (
            <div className="flex items-center gap-2 text-cobalt"><Loader2 className="w-4 h-4 animate-spin" /> Loading pipelines from HubSpot…</div>
          )}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-600 p-3 text-sm text-red-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {!loading && !error && mapping && (
            <>
              {/* PIPELINE selector */}
              <section>
                <h3 className="text-sm uppercase tracking-[0.18em] font-bold text-cobalt mb-2">1. Pipeline</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {pipelines.map((p) => (
                    <button key={p.id}
                            onClick={() => switchPipeline(p.id)}
                            data-testid={`hubspot-pipeline-${p.id}`}
                            className={`px-3 py-1.5 text-sm border-2 ${mapping.pipeline_id === p.id ? "border-navy bg-navy text-warm" : "border-navy/30 text-navy hover:border-navy"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* STAGES */}
              <section>
                <h3 className="text-sm uppercase tracking-[0.18em] font-bold text-cobalt mb-2">2. Stage → funnel bucket</h3>
                <p className="text-xs opacity-70 mb-3">Tick every bucket each stage belongs to. <em>Meeting Sat</em> is Held + Qualified by default (counts in both show-up and qualified-leads calcs).</p>
                <div className="overflow-x-auto">
                  <table className="il-table w-full text-sm" data-testid="hubspot-stages-table">
                    <thead>
                      <tr>
                        <th className="text-left">HubSpot stage</th>
                        {bucketDefs.map((b) => (
                          <th key={b.id} className="text-center text-[10px] uppercase tracking-widest px-2">{b.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPipeline?.stages.map((s) => {
                        const selected = new Set(mapping.stage_buckets?.[s.id] || []);
                        return (
                          <tr key={s.id} data-testid={`hubspot-stage-row-${s.id}`}>
                            <td className="font-medium text-navy">{s.label}</td>
                            {bucketDefs.map((b) => (
                              <td key={b.id} className="text-center">
                                <input type="checkbox"
                                       checked={selected.has(b.id)}
                                       onChange={() => toggleBucketForStage(s.id, b.id)}
                                       data-testid={`hubspot-bucket-${s.id}-${b.id}`}
                                       className="w-4 h-4 accent-navy cursor-pointer" />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* SOURCE map */}
              <section>
                <h3 className="text-sm uppercase tracking-[0.18em] font-bold text-cobalt mb-2">3. Qualified Calls · definition</h3>
                <p className="text-xs opacity-70 mb-3">
                  &ldquo;Qualified&rdquo; is a sales-team judgment after the call, not a stage event. Pick how to count it.
                </p>
                <div className="space-y-2">
                  {QUAL_MODES.map((q) => {
                    const active = (mapping.qualified_def?.mode || "none") === q.id;
                    return (
                      <label key={q.id}
                             className={`block border-2 p-3 cursor-pointer ${active ? "border-navy bg-warm" : "border-navy/20 hover:border-navy/40"}`}
                             data-testid={`hubspot-qual-mode-${q.id}`}>
                        <div className="flex items-start gap-3">
                          <input type="radio" name="qual-mode" checked={active}
                                 onChange={() => setMapping((m) => ({ ...m, qualified_def: { ...(m.qualified_def || {}), mode: q.id } }))}
                                 className="mt-1 w-4 h-4 accent-navy" />
                          <div className="flex-1">
                            <div className="font-bold text-navy text-sm">{q.label}</div>
                            <div className="text-xs opacity-70 mt-0.5">{q.desc}</div>
                            {active && q.id === "property" && (
                              <div className="mt-3 flex flex-col gap-2">
                                <label className="text-[10px] uppercase tracking-widest text-cobalt">Deal property</label>
                                <select value={mapping.qualified_def?.property || ""}
                                        onChange={(e) => setMapping((m) => ({ ...m, qualified_def: { ...(m.qualified_def || {}), property: e.target.value, values: [] } }))}
                                        className="border-2 border-navy/30 px-2 py-1 bg-warm-tint text-navy text-sm w-full max-w-md"
                                        data-testid="hubspot-qual-property">
                                  <option value="">— pick a property —</option>
                                  {dealProps.filter((p) => !p.hubspot_defined || ["dealstage","pipeline","amount","closedate","createdate","dealname","hubspot_owner_id"].indexOf(p.name) === -1).map((p) => (
                                    <option key={p.name} value={p.name}>{p.label} ({p.name}) · {p.type}</option>
                                  ))}
                                </select>
                                {mapping.qualified_def?.property && (() => {
                                  const prop = dealProps.find((p) => p.name === mapping.qualified_def.property);
                                  const opts = prop?.options || [];
                                  if (opts.length === 0) {
                                    return (
                                      <div className="text-xs">
                                        <label className="text-[10px] uppercase tracking-widest text-cobalt block mb-1">Values that count as qualified (comma-separated)</label>
                                        <input type="text"
                                               value={(mapping.qualified_def.values || []).join(", ")}
                                               onChange={(e) => setMapping((m) => ({ ...m, qualified_def: { ...m.qualified_def, values: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } }))}
                                               className="border-2 border-navy/30 px-2 py-1 bg-white text-navy w-full max-w-md"
                                               placeholder="e.g. true, Yes, Qualified"
                                               data-testid="hubspot-qual-values-text" />
                                      </div>
                                    );
                                  }
                                  const selected = new Set(mapping.qualified_def.values || []);
                                  return (
                                    <div className="text-xs">
                                      <label className="text-[10px] uppercase tracking-widest text-cobalt block mb-1">Values that count as qualified</label>
                                      <div className="flex flex-wrap gap-2">
                                        {opts.map((o) => (
                                          <label key={o.value}
                                                 className={`px-2 py-1 border-2 cursor-pointer ${selected.has(o.value) ? "border-navy bg-navy text-warm" : "border-navy/30 text-navy"}`}>
                                            <input type="checkbox" className="hidden"
                                                   checked={selected.has(o.value)}
                                                   onChange={() => {
                                                     setMapping((m) => {
                                                       const cur = new Set(m.qualified_def.values || []);
                                                       if (cur.has(o.value)) cur.delete(o.value); else cur.add(o.value);
                                                       return { ...m, qualified_def: { ...m.qualified_def, values: Array.from(cur) } };
                                                     });
                                                   }} />
                                            {o.label}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </section>

              {/* SOURCE map */}
              <section>
                <h3 className="text-sm uppercase tracking-[0.18em] font-bold text-cobalt mb-2">4. <code>hs_analytics_source</code> → lead-source pie</h3>
                <p className="text-xs opacity-70 mb-3">HubSpot&rsquo;s built-in original source values map to our 6 pie buckets. Override any row you want.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {Object.keys({ ...defaultSrcMap, ...(mapping.source_value_map || {}) }).map((hsVal) => (
                    <div key={hsVal} className="flex items-center gap-3" data-testid={`hubspot-srcmap-row-${hsVal}`}>
                      <code className="text-cobalt text-xs flex-1">{hsVal}</code>
                      <span className="opacity-50">→</span>
                      <select value={mapping.source_value_map?.[hsVal] || "Other"}
                              onChange={(e) => updateSourceMap(hsVal, e.target.value)}
                              data-testid={`hubspot-srcmap-${hsVal}`}
                              className="border-2 border-navy/30 px-2 py-1 bg-warm-tint text-navy">
                        {ALL_SOURCES.map((src) => <option key={src} value={src}>{src}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </section>

              {/* PREVIEW */}
              <section>
                <h3 className="text-sm uppercase tracking-[0.18em] font-bold text-cobalt mb-2 flex items-center gap-2">
                  <Eye className="w-4 h-4" /> 5. Sanity-check · 5 most recent deals
                </h3>
                <div className="overflow-x-auto">
                  <table className="il-table w-full text-xs" data-testid="hubspot-preview-table">
                    <thead>
                      <tr>
                        <th>Deal</th>
                        <th>$</th>
                        <th>Stage ID</th>
                        <th>Source</th>
                        <th>Close date</th>
                        <th>Package guess</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((d) => (
                        <tr key={d.id} data-testid={`hubspot-preview-row-${d.id}`}>
                          <td className="text-navy">{d.dealname || `Deal ${d.id}`}</td>
                          <td className="text-evergreen font-semibold">{d.amount ? `$${Number(d.amount).toLocaleString()}` : "—"}</td>
                          <td><code className="text-[10px] opacity-70">{d.dealstage}</code></td>
                          <td>{d.hs_analytics_source || "—"}</td>
                          <td>{d.closedate ? new Date(d.closedate).toLocaleDateString() : "—"}</td>
                          <td className="font-semibold">{d.package_guess || <span className="opacity-50">—</span>}</td>
                        </tr>
                      ))}
                      {!preview.length && (
                        <tr><td colSpan={6} className="text-center opacity-60 py-4">No recent deals returned. Verify token + scopes.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* SAVE */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2 items-stretch sm:items-center">
                <button onClick={() => save(false)} disabled={busy}
                        className="btn-cobalt inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="hubspot-mapping-save">
                  <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save mapping (stay read-only)"}
                </button>
                <button onClick={() => save(true)} disabled={busy}
                        className="btn-yellow inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="hubspot-mapping-activate">
                  <ShieldCheck className="w-4 h-4" /> {busy ? "Activating…" : "Save & activate HubSpot as primary"}
                </button>
                {saveMsg && (
                  <div className={`text-sm flex items-center gap-2 ${saveMsg.kind === "ok" ? "text-evergreen" : "text-red-700"}`}
                       data-testid="hubspot-mapping-msg">
                    {saveMsg.kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {saveMsg.text}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
